
import React from 'react';
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Link } from "react-router-dom";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { ConstructionMethodsDialog } from "@/components/channel/ConstructionMethodsDialog";
import { ModularConstructionDialog } from "@/components/channel/ModularConstructionDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import KnowledgeNetworkStats from "@/components/shared/KnowledgeNetworkStats";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguage } from "@/contexts/LanguageContext";
const domains = [{
  symbol: "Project Management",
  name: "Project Management",
  categories: "Project Scoping, Scheduling, Resource Allocation, Risk Management, Budgeting"
}, {
  symbol: "Engineering & Design",
  name: "Engineering & Design",
  categories: "Structural Design, Civil Engineering, MEP, Architectural Design"
}, {
  symbol: "Construction Methods",
  name: "Construction Methods",
  categories: "Modular Construction, Prefabrication, 3D Printing, Traditional Construction"
}, {
  symbol: "Supply Chain",
  name: "Supply Chain",
  categories: "Vendor Management, Material Sourcing, Contract Negotiation"
}, {
  symbol: "Green Building",
  name: "Green Building",
  categories: "LEED Certification, Sustainable Materials, Carbon Footprint Reduction"
}, {
  symbol: "Automation",
  name: "Automation",
  categories: "BIM, Digital Twins, Construction Robotics, AI-driven Management"
}, {
  symbol: "Health & Safety",
  name: "Health & Safety",
  categories: "Safety Standards, Hazardous Material Management, Emergency Response"
}, {
  symbol: "Financial Control",
  name: "Financial Control",
  categories: "Value Engineering, Cost Forecasting, EVM, Financial Risk Management"
}, {
  symbol: "Quality Assurance",
  name: "Quality Assurance",
  categories: "Inspection, Testing, ISO Certification, Quality Audits"
}, {
  symbol: "Innovation Tech",
  name: "Innovation Tech",
  categories: "Smart Buildings, Robotics, AI Optimization"
}, {
  symbol: "Workforce Dev",
  name: "Workforce Dev",
  categories: "Skills Development, Safety Training, Digital Skills, Project Management"
}, {
  symbol: "Sustainability",
  name: "Sustainability",
  categories: "Sustainable Practices, Waste Reduction, Energy Efficiency"
}, {
  symbol: "Auto Construction",
  name: "Auto Construction",
  categories: "Drones, Autonomous Vehicles, Robotics, IoT Monitoring"
}, {
  symbol: "Data Analytics",
  name: "Data Analytics",
  categories: "Real-time Monitoring, Predictive Analytics, AI Optimization"
}, {
  symbol: "Stakeholder Eng",
  name: "Stakeholder Eng",
  categories: "Client Communication, Change Management, Reporting Systems"
}];
const networkStats = {
  totalParticipants: 1087,
  activeContributors: 542,
  knowledgeBases: 856,
  totalSolutions: 298,
  departments: [{
    name: "Project Management",
    participants: 245
  }, {
    name: "Engineering & Design",
    participants: 213
  }, {
    name: "Construction Methods",
    participants: 187
  }, {
    name: "Green Building",
    participants: 156
  }, {
    name: "Innovation & Technology",
    participants: 286
  }]
};
const CurtChannel = () => {
  const { t } = useTranslation();
  const { currentLanguage, setLanguage } = useLanguage();
  const getExampleKnowledgeBases = (categories: string, domainSymbol: string) => {
    if (domainSymbol === "Construction Methods") {
      const categoriesArray = categories.split(", ");
      return categoriesArray.map(category => {
        if (category === "Modular Construction") {
          return `${category}: Click to explore 25 knowledge bases on modular construction techniques and best practices`;
        }
        const examples = category.toLowerCase().includes("optimization") ? "Best practices, case studies, and technical documentation for optimization strategies" : category.toLowerCase().includes("management") ? "Standard operating procedures, workflow guides, and management frameworks" : "Technical specifications, implementation guides, and industry standards";
        return `${category}: ${examples}`;
      }).join("\n");
    }
    return categories.split(", ").map(category => {
      const examples = category.toLowerCase().includes("optimization") ? "Best practices, case studies, and technical documentation for optimization strategies" : category.toLowerCase().includes("management") ? "Standard operating procedures, workflow guides, and management frameworks" : "Technical specifications, implementation guides, and industry standards";
      return `${category}: ${examples}`;
    }).join("\n");
  };
  return <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <img src="/lovable-uploads/49b60a3b-0a14-46be-81a8-e58b33f9b383.png" alt="CURT Logo" className="w-24 h-24 object-contain cursor-pointer hover:opacity-80 transition-opacity" />
                <div>
                  <CardTitle className="text-2xl font-bold mb-2">{t('curt.title')}</CardTitle>
                  <CardDescription className="text-lg">
                    {t('curt.description')}
                  </CardDescription>
                  <Button variant="link" className="p-0 h-auto text-primary hover:text-primary/80 flex items-center gap-2" onClick={() => window.open('https://www.curt.org/about-us/about-curt/', '_blank')}>
                    {t('curt.visitWebsite')}
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/demo-call-for-solutions">
                        <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                          CfS
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Solution</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/call-for-destruction-bounty">
                        <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                          CfD
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Destruction</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <Popover>
                  <PopoverTrigger>
                    <img src="/lovable-uploads/0714ee51-a10f-4464-b167-1ad5ca4fb1c1.png" alt="ASK CURT Logo" className="h-16 object-contain cursor-pointer hover:opacity-80 transition-opacity" />
                  </PopoverTrigger>
                  <PopoverContent className="w-[800px] p-0">
                    <img src="/lovable-uploads/a1ad3186-f15d-4670-8b93-388a6ed7979f.png" alt="AskCURT Information" className="w-full h-auto rounded-md" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <h2 className="text-xl font-semibold mb-4"></h2>
            <KnowledgeNetworkStats {...networkStats} channelName="curt" />

            <h2 className="text-xl font-semibold mb-4">{t('curt.domainElements')}</h2>
            <div className="grid grid-cols-5 gap-2">
              {domains.map(domain => <TooltipProvider key={domain.symbol} delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {domain.symbol === "Construction Methods" ? <Dialog>
                          <DialogTrigger asChild>
                            <div className="bg-purple-600 text-white p-4 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg">
                              <div className="text-sm font-bold text-center mb-1">{domain.name}</div>
                            </div>
                          </DialogTrigger>
                          <ConstructionMethodsDialog />
                        </Dialog> : <div className="bg-primary text-primary-foreground p-4 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg">
                          <div className="text-sm font-bold text-center mb-1">{domain.name}</div>
                        </div>}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-white text-black border border-gray-200 max-w-[400px] whitespace-pre-line">
                      <p className="font-semibold mb-2">{domain.name}</p>
                      <p className="text-sm">Representative Knowledge Bases:</p>
                      <p className="text-xs mt-1">
                        {domain.symbol === "Construction Methods" ? <>
                            Modular Construction: <Link to="/modular-construction" className="text-blue-600 hover:underline">Click to explore 25 knowledge bases</Link><br />
                            Prefabrication: Technical specifications, implementation guides, and industry standards<br />
                            3D Printing: Technical specifications, implementation guides, and industry standards<br />
                            Traditional Construction: Technical specifications, implementation guides, and industry standards
                          </> : getExampleKnowledgeBases(domain.categories, domain.symbol)}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>)}
            </div>

            <h2 className="text-xl font-semibold mt-8 mb-4">{t('curt.industryTemplates')}</h2>
            <div className="grid grid-cols-3 gap-4">
              <div onClick={() => window.open('https://www.ispai.io/_files/ugd/2d66bb_aefbd8b72cdf4bba9426960e3f237731.pdf', '_blank')} className="bg-white border-2 border-black p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <img src="/lovable-uploads/9943d1c9-6a3a-412a-8952-120fd044fb79.png" alt="Construction Industry Template" className="max-w-full h-auto mx-auto" />
              </div>
              <div onClick={() => window.open('https://www.ispai.io/_files/ugd/2d66bb_1dca9e81124a4d6fb00b6949cafc0303.pdf', '_blank')} className="bg-white border-2 border-black p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <img src="/lovable-uploads/c394fc73-a823-469d-ba30-b61f806e5da9.png" alt="Construction Trades Industry Template" className="max-w-full h-auto mx-auto" />
              </div>
              <div onClick={() => window.open('https://www.ispai.io/_files/ugd/2d66bb_bf019caee4fd4fbb825db155296fa830.pdf', '_blank')} className="bg-white border-2 border-black p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <img src="/lovable-uploads/15fbea79-bd2b-460b-b7b2-a42c7f93e89a.png" alt="Capital Projects Industry Template" className="max-w-full h-auto mx-auto" />
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>;
};
export default CurtChannel;
